import React, { useCallback, useContext } from "react";

import { SOURCE_TYPES_EMARSYS } from "@catchoftheday/analytics";
import { AddToCartButton } from "@catchoftheday/cart-components";
import {
  Box,
  Flex,
  FlexProps,
  ImageResponsive,
  Link,
  LinkButton,
} from "@catchoftheday/cg-components";
import {
  CartContext,
  CartContextDocument,
} from "@catchoftheday/common-vendors";
import { CustomerContext } from "@catchoftheday/customer";
import {
  isActiveOnePassMember,
  OnePassJoinToBuyButton,
} from "@catchoftheday/onepass";
import { triggerOnePassFreeShippingDrawer } from "@catchoftheday/p13n-integration";
import { styled, theme, themeGet } from "@catchoftheday/theme";
import {
  centsToDollars,
  getEffectiveUrl,
  resizeImage,
} from "@catchoftheday/utilities";

import { ProductPrice } from "../ProductPrice";

import { ProductCardTags } from "./ProductCardTags/ProductCardTags";
import { AvailabilityTags } from "./AvailabilityTags";
import { ProductCardBrand } from "./ProductCardBrand";
import { ProductCardRating } from "./ProductCardRating";
import { ProductCardTitle } from "./ProductCardTitle";
import { ProductItemType } from "./productCardTypes";

const PRODUCT_IMAGE_ASPECT_RATIO = 81.818;

export type ProductCardProps = Omit<FlexProps, "ref"> & {
  lazyLoad?: boolean;
  visibleActionButton?: boolean;
  productItem: ProductItemType;
  onAddToCart?: () => void;
  onProductClick?: () => void;
};

type ProductCardContainerProps = FlexProps & { visibleActionButton?: boolean };

const ProductCardContainer = styled(Flex)<ProductCardContainerProps>`
  & div#atcbtn {
    transition: bottom 0.2s ease-out;
    position: absolute;
    bottom: ${(props) => (!props.visibleActionButton ? "-120px" : 0)};
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.8);
    z-index: 5;
  }

  @media only screen and (min-width: ${themeGet("breakpoints.lg")}) {
    &:hover {
      & div#atcbtn {
        bottom: 0;
      }
      > div {
        border: 1px solid ${themeGet("colors.secondary")};
      }
    }
  }
`;

const PriceLink = styled(Link)`
  &:hover,
  &:focus {
    text-decoration: none;
  }
`;

export const ProductCard = ({
  lazyLoad,
  productItem,
  visibleActionButton,
  onProductClick,
  onAddToCart,
  ...flexProps
}: ProductCardProps) => {
  const {
    attributeName,
    averageRating,
    brand,
    clubCatchEligible,
    freeShippingAvailable,
    id,
    offerId,
    ratingCount,
    sourceId,
    sourcePosition,
    sourceType,
    title,
    imageLocation,
    priceTag,
    badges,
    clubCatchExclusive,
    sellerId,
    adult,
    sellPrice,
  } = productItem;

  const url = getEffectiveUrl(productItem);
  const { data: customerData } = useContext(CustomerContext);
  const { data: cartData } = useContext(CartContext);

  const emarsysAttributionTag =
    typeof sourceType === "number" && SOURCE_TYPES_EMARSYS.includes(sourceType)
      ? id
      : undefined;

  const handleAddToCartClick = useCallback(() => {
    const { orderSummary } = cartData;
    const { itemSummary, totalPrice } = orderSummary;
    const { items } = itemSummary;

    if (
      sellPrice &&
      sellPrice <= 7500 &&
      totalPrice.amount <= 7500 &&
      clubCatchEligible &&
      isActiveOnePassMember(customerData)
    ) {
      triggerOnePassFreeShippingDrawer({
        productId: parseInt(id),
        offerId: offerId ? parseInt(offerId) : undefined,
        price: centsToDollars(sellPrice),
        sellerId: sellerId ? parseInt(sellerId) : undefined,
        cartItemProductIds: items.map(
          (item) => item.offer?.variant?.product?.id
        ),
        isAdult: adult,
      });
    }

    if (onAddToCart) {
      onAddToCart();
    }
  }, [
    adult,
    cartData,
    clubCatchEligible,
    customerData,
    id,
    offerId,
    onAddToCart,
    sellPrice,
    sellerId,
  ]);

  return (
    <ProductCardContainer
      id={`${id}`}
      paddingX={{ xs: "2px", md: "10px" }}
      textAlign="center"
      data-scarabitem={emarsysAttributionTag}
      visibleActionButton={visibleActionButton}
      height="100%"
      {...flexProps}
    >
      <Flex
        flexDirection="column"
        width="100%"
        position="relative"
        overflow="hidden"
        border={`1px solid ${theme.colors.ui.borderColor}`}
        backgroundColor="white"
        fontWeight="light"
        data-ts="product-card"
      >
        <Box display="block" position="relative" overflow="hidden" mb={0}>
          <Link
            href={url}
            title={`View ${title}`}
            hoverColor="currentColor"
            data-product-id={id}
            data-ts="product-card-link"
            onClick={onProductClick}
          >
            <ImageResponsive
              alt={title}
              lazyLoad={lazyLoad}
              sourceWidths={[100, 200, 300, 400, 500]}
              src={resizeImage(imageLocation)}
              ratio={PRODUCT_IMAGE_ASPECT_RATIO}
            />
            {badges && <AvailabilityTags badges={badges} />}
          </Link>
          {!visibleActionButton && offerId && (
            <Box padding="10px" id="atcbtn">
              {attributeName ? (
                <LinkButton
                  block
                  sizeVariant="sm"
                  href={url}
                  title={attributeName}
                  variant="secondary"
                  outline
                  fontSize="xs"
                >
                  Select {attributeName}
                </LinkButton>
              ) : clubCatchExclusive && !isActiveOnePassMember(customerData) ? (
                <OnePassJoinToBuyButton fontSize="xs" block />
              ) : (
                <AddToCartButton
                  variant="primary"
                  block
                  color=""
                  p=""
                  textTransform="uppercase"
                  fontSize="xs"
                  underlineOnHover={false}
                  imageUrl={imageLocation}
                  offerId={offerId}
                  sourceId={sourceId}
                  sourcePosition={sourcePosition}
                  sourceType={sourceType}
                  title={title}
                  quantity={1}
                  onClick={handleAddToCartClick}
                  refetchQueries={[
                    {
                      query: CartContextDocument,
                    },
                  ]}
                  awaitRefetchQueries
                >
                  Add to Cart
                </AddToCartButton>
              )}
            </Box>
          )}
        </Box>
        <Flex
          flexGrow={1}
          flexDirection="column"
          justifyContent="space-between"
          py="6px"
        >
          <Box>
            <ProductCardTitle
              url={url}
              productId={id}
              title={title}
              onClick={onProductClick}
            />
            {brand && <ProductCardBrand brand={brand} />}
          </Box>
          <PriceLink
            display="table"
            px={{ md: "10px" }}
            pb="6px"
            width="100%"
            textDecoration="none"
            href={url}
            title={`View ${title}`}
            data-product-id={id}
            hoverColor="inherit"
            onClick={onProductClick}
          >
            {priceTag && <ProductPrice priceTag={priceTag} />}
          </PriceLink>
          <Flex
            flexDirection={"row"}
            justifyContent="space-around"
            px={{ xs: "2px", md: "36px" }}
            alignItems="center"
            minHeight="45px"
          >
            {(clubCatchEligible || freeShippingAvailable) && (
              <ProductCardTags
                onePassEligible={clubCatchEligible}
                freeShippingAvailable={freeShippingAvailable}
              />
            )}
            {!!averageRating && averageRating >= 1 && ratingCount > 0 && (
              <ProductCardRating
                url={url}
                productId={id}
                rating={averageRating}
                ratingCount={ratingCount}
              />
            )}
          </Flex>
          {visibleActionButton && offerId && (
            <Box id="atcbtn">
              {attributeName ? (
                <LinkButton
                  block
                  sizeVariant="sm"
                  href={url}
                  title={`Select ${attributeName}`}
                  variant="secondary"
                  outline
                  fontSize="xs"
                >
                  Select {attributeName}
                </LinkButton>
              ) : clubCatchExclusive && !isActiveOnePassMember(customerData) ? (
                <OnePassJoinToBuyButton fontSize="xs" block />
              ) : (
                <AddToCartButton
                  variant="tertiary"
                  imageUrl={imageLocation}
                  offerId={offerId}
                  sourceId={sourceId}
                  sourcePosition={sourcePosition}
                  sourceType={sourceType}
                  title={title}
                  quantity={1}
                  onClick={onAddToCart}
                >
                  Add to Cart
                </AddToCartButton>
              )}
            </Box>
          )}
        </Flex>
      </Flex>
    </ProductCardContainer>
  );
};
